import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useApi } from 'react-ui';
import { Combobox, Transition } from '@headlessui/react';
import React from 'react';
import { BalloonEventModel, LngLatGeolocation, apiFetch } from 'ts-core';
import {
  useTheme,
  useTranslations,
  useQuery,
  Spinner,
  useBalloonEventsMetadata,
} from 'react-ui';
import { useApplyTagFilter } from '../hooks/useApplyTagFilter';
import { useMapBox } from './BalloonEventsMapBox';
import mapboxgl from 'mapbox-gl';
import { trackMixpanel } from '../utils/mixpanel';
import { useIsMobile } from "react-ui";

import { useMapBoundingBox } from '../contexts/MapBoundingBox.tsx';

import { PiMapPinAreaLight } from 'react-icons/pi';
import { PiBalloonLight } from 'react-icons/pi';
import { PiBuildingLight } from 'react-icons/pi';
import { PiMapPinLight } from 'react-icons/pi';
import CloseSearchIcon from '../../assets/close-search-icon.svg?react';
import SearchIcon from '../../assets/search-icon.svg?react';
import ChevronSelectorIcon from '../../assets/chevron-selector-icon.svg?react';
import CheckmarkIcon from '../../assets/checkmark-icon.svg?react';
import HashIcon from '../../assets/hash.svg?react';

interface Region {
  id: string;
  name: string;
  slug: string;
  type: 'grouping' | 'region';
}

interface Venue {
  id: string;
  name: string;
  street: string | null;
  zip_code: string | null;
  address_locality: string | null;
  full_address: string | null;
  coordinates: { lat: number; lon: number } | null;
}

interface AddressSuggestion {
  id: string;
  name: string;
  detail: string;
  placeName: string;
  center: [number, number];
}

// How long the input stays idle (no keystrokes) before the parent text-filter
// query — and the /v3/search round-trip it triggers — actually fires. Anything
// shorter and a fast typist sends one request per keystroke, which both jams
// the network and stalls the UI while the EventDataProvider re-runs against a
// 2000-event cache.
const SEARCH_DEBOUNCE_MS = 300;

export default function SearchBar({
  events,
  selectedEvent,
  selectEvent,
  searchText: searchTextProp,
  setSearchText,
  highlight,
  loading,
  embedded = false,
}: {
  events: BalloonEventModel[];
  selectedEvent: BalloonEventModel | null;
  selectEvent: (id: string) => void;
  searchText: string;
  setSearchText: (text: string) => void;
  highlight: boolean;
  loading: boolean;
  embedded?: boolean;
}) {
  // Mirror the prop into local state so the input updates instantly on
  // keystroke. The parent's `setSearchText` (which fires addQuery → /v3/search)
  // is called via a 300ms debounce below — without that, every keystroke
  // re-runs the EventDataProvider effect against the full 2000-event cache and
  // the browser stalls visibly.
  const [searchText, setSearchTextLocal] = useState(searchTextProp);
  useEffect(() => {
    setSearchTextLocal(searchTextProp);
  }, [searchTextProp]);

  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
  }, []);

  const searchMode: 'tag' | 'location' | 'default' =
    searchText.startsWith('#')
      ? 'tag'
      : searchText.startsWith('@')
        ? 'location'
        : 'default';
  const searchQuery =
    searchMode !== 'default' ? searchText.slice(1) : searchText;

  const filteredEvents = useMemo(() => {
    if (searchMode !== 'default') return [];
    const needle = searchText.toLowerCase();
    if (!needle) return [];
    return events
      .filter(
        (e) =>
          e.title.toLowerCase().includes(needle) ||
          (e.description != null && e.description.includes(searchText)) ||
          (e.teaser != null && e.teaser.includes(searchText))
      )
      .slice(0, 10);
  }, [events, searchText, searchMode]);
  const { width, height, top, left } = useMapBoundingBox();
  const { primaryColor } = useTheme();
  const { translated, language } = useTranslations();
  const { addQuery, has, removeQueryById, queries, query } = useQuery();
  const { apiEndpoints, apiKey } = useApi();
  const applyTag = useApplyTagFilter();
  const mapBoxInfo = useMapBox();
  const map = mapBoxInfo?.map;
  const [regionSuggestions, setRegionSuggestions] = useState<Region[]>([]);
  const [venueSuggestions, setVenueSuggestions] = useState<Venue[]>([]);
  const [addressSuggestions, setAddressSuggestions] = useState<
    AddressSuggestion[]
  >([]);
  const [centerMarker, setCenterMarker] = useState<mapboxgl.Marker | null>(
    null
  );
  const [selectedFilterId, setSelectedFilterId] = useState<string | null>(null);
  const [displayLabel, setDisplayLabel] = useState<string | null>(null);
  const isMobile = useIsMobile();
  const { visibleTags, metadata } = useBalloonEventsMetadata();

  const filteredTags =
    (searchMode === 'tag' || searchMode === 'default') && !selectedFilterId && searchQuery !== ''
      ? visibleTags
        .filter((tag) =>
          tag.name.toLowerCase().startsWith(searchQuery.toLowerCase())
        )
        .slice(0, 5)
      : [];
  const clearTextQuery = () => {
    setDisplayLabel(null);
    setSearchTextLocal('');
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    setSearchText('');
    removeQueryById('text');
    if (selectedFilterId) {
      removeQueryById(selectedFilterId);
      setSelectedFilterId(null);
    }
    if (centerMarker) {
      centerMarker.remove();
      setCenterMarker(null);
    }
  };

  const getRegionCenter = (regionSlug: string, zoom: number) => {
    if (regionSlug != '' && apiKey) {
      apiFetch(apiEndpoints.region + '/' + regionSlug + '/coordinates', {
        method: 'GET',
        apiKey,
      })
        .then((res) => {
          if (!res.ok) {
            console.warn('[SearchBar] Failed to fetch region center:', regionSlug, res.status);
            return null;
          }
          return res.json();
        })
        .then((r) => {
          if (!r) return;
          if (!map) return;
          if (centerMarker) {
            centerMarker.remove();
          }
          setCenterMarker(
            new mapboxgl.Marker({
              color: primaryColor,
              scale: 1.5,
            })
              .setLngLat(r)
              .addTo(map)
          );
          map.flyTo({
            center: r,
            zoom,
          });
        })
        .catch((error) => {
          console.error('[SearchBar] getRegionCenter failed:', error);
        });
    }
  };
  const onChange = (val: string | null) => {
    if (!val) return;

    // Check if it's an address selection (raw Mapbox geocode result — pure
    // map navigation, not a saved-event filter).
    if (val.startsWith('address:')) {
      const selectedAddress = addressSuggestions.find((a) => a.id === val);
      if (selectedAddress && map) {
        if (centerMarker) {
          centerMarker.remove();
        }
        setCenterMarker(
          new mapboxgl.Marker({
            color: primaryColor,
            scale: 1.5,
          })
            .setLngLat(selectedAddress.center)
            .addTo(map)
        );
        map.flyTo({
          center: selectedAddress.center,
          zoom: 15,
        });
        setDisplayLabel(selectedAddress.placeName);
        removeQueryById('text');
        if (selectedFilterId) {
          removeQueryById(selectedFilterId);
          setSelectedFilterId(null);
        }
        trackMixpanel('SearchBar', 'SelectAddress', {
          address: selectedAddress.placeName,
        });
      }
      return;
    }

    // Check if it's a venue selection
    const selectedVenue = venueSuggestions.find((v) => v.id === val);
    if (selectedVenue && selectedVenue.coordinates && map) {
      if (centerMarker) {
        centerMarker.remove();
      }
      const coords: [number, number] = [
        selectedVenue.coordinates.lon,
        selectedVenue.coordinates.lat,
      ];
      setCenterMarker(
        new mapboxgl.Marker({
          color: primaryColor,
          scale: 1.5,
        })
          .setLngLat(coords)
          .addTo(map)
      );
      map.flyTo({
        center: coords,
        zoom: 13,
      });
      setDisplayLabel(selectedVenue.name);
      removeQueryById('text');
      setSelectedFilterId(`venue:${selectedVenue.id}`);
      trackMixpanel('SearchBar', 'SelectVenue', {
        venueId: val,
        venueName: selectedVenue.name,
      });
      return;
    }

    // Check if it's a region selection
    const selectedRegion = regionSuggestions.find((r) => r.id === val);
    if (selectedRegion) {
      const regionZoom = 11;
      getRegionCenter(selectedRegion.slug, regionZoom);
      setDisplayLabel(selectedRegion.name);
      removeQueryById('text');
      setSelectedFilterId(`region:${selectedRegion.slug}`);
      trackMixpanel('SearchBar', 'SelectRegion', {
        regionId: selectedRegion.id,
        regionName: selectedRegion.name,
      });
      return;
    }

    // Check if it's a tag selection
    const selectedTag = filteredTags.find((t) => `tag:${t.slug}` === val);
    if (selectedTag) {
      // Shared hook: bare tag layer is added immediately (optimistic) and the
      // tag-bound saved-search merge (forceMapClustering, amount, ...) replaces
      // it once resolved. Same code path as /tags/:slug and banner clicks.
      void applyTag(selectedTag.slug, { optimistic: true });
      // Clear the input both locally (immediate) and via the parent setter
      // (also cancel any pending debounced text update so we don't immediately
      // re-add the just-typed text right after picking the tag).
      setSearchTextLocal('');
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
      setSearchText('');
      setDisplayLabel(null);
      removeQueryById('text');
      trackMixpanel('SearchBar', 'SelectTag', {
        tagId: selectedTag.id,
        tagName: selectedTag.name,
      });
      return;
    }

    // Otherwise it's an event selection
    selectEvent(val);
    trackMixpanel('SearchBar', 'SelectEvent', {
      eventId: val,
      eventName: filteredEvents.find((e) => e.id === val)?.title,
    });
  };

  useEffect(() => {
    if (searchMode === 'tag') {
      setVenueSuggestions([]);
      setRegionSuggestions([]);
      return;
    }
    const locationQuery = searchMode === 'location' ? searchQuery : searchText;
    if (locationQuery !== '' && apiKey) {
      apiFetch(apiEndpoints.regions + '?name=' + locationQuery, {
        method: 'GET',
        apiKey,
      })
        .then((res) => res.json())
        .then((r) => {
          const seenVenues = new Set<string>();
          const uniqueVenues: Venue[] = (r.venues ?? []).filter((v: Venue) => {
            const key = `${v.name}|${v.zip_code ?? ''}|${v.address_locality ?? ''}`.toLowerCase();
            if (seenVenues.has(key)) return false;
            seenVenues.add(key);
            return true;
          });
          setVenueSuggestions(uniqueVenues.slice(0, 5));
          const groupings: Region[] = (r.regionGroupings ?? []).map((g: Omit<Region, 'type'>) => ({ ...g, type: 'grouping' as const }));
          const groupingNames = new Set(groupings.map((g) => g.name));
          const filteredRegions: Region[] = (r.regions ?? [])
            .filter((reg: Omit<Region, 'type'>) => !groupingNames.has(reg.name))
            .map((reg: Omit<Region, 'type'>) => ({ ...reg, type: 'region' as const }));
          const combined = [...groupings, ...filteredRegions];
          const seen = new Set<string>();
          const unique = combined.filter((item) => {
            if (seen.has(item.name)) return false;
            seen.add(item.name);
            return true;
          });
          setRegionSuggestions(unique.slice(0, 5));
        });
    } else {
      setVenueSuggestions([]);
      setRegionSuggestions([]);
    }
  }, [searchMode, searchQuery, searchText, apiKey, apiEndpoints.regions]);

  // Forward-geocode the typed text into address/POI suggestions via Mapbox
  // (same Geocoding v5 API the map context-menu uses for reverse lookups).
  // Debounced + aborted because, unlike the region endpoint, this hits a
  // rate-limited, billed third-party API and we don't want one request per
  // keystroke. Results are biased toward the current map center via proximity.
  useEffect(() => {
    if (searchMode === 'tag') {
      setAddressSuggestions([]);
      return;
    }
    const locationQuery = (
      searchMode === 'location' ? searchQuery : searchText
    ).trim();
    const token = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN;
    if (locationQuery.length < 3 || !token) {
      setAddressSuggestions([]);
      return;
    }

    const controller = new AbortController();
    const timer = setTimeout(() => {
      const params = new URLSearchParams({
        access_token: token,
        autocomplete: 'true',
        limit: '5',
        types: 'address,poi',
        language,
      });
      const center = map?.getCenter();
      if (center) {
        params.set('proximity', `${center.lng},${center.lat}`);
      }
      fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(
          locationQuery
        )}.json?${params.toString()}`,
        { signal: controller.signal }
      )
        .then((res) => (res.ok ? res.json() : null))
        .then((data) => {
          if (!data?.features) {
            setAddressSuggestions([]);
            return;
          }
          const suggestions: AddressSuggestion[] = data.features
            .filter(
              (f: { center?: unknown }) =>
                Array.isArray(f.center) && f.center.length === 2
            )
            .slice(0, 5)
            .map((f: { id: string; text?: string; place_name?: string; center: [number, number] }) => {
              const parts = (f.place_name ?? '').split(', ');
              // Use the first place_name segment ("Praterstraße 15") rather than
              // `f.text` ("Praterstraße") so the house number is shown when the
              // geocoder matched one.
              return {
                id: `address:${f.id}`,
                name: parts[0] || f.text || '',
                detail: parts.slice(1).join(', '),
                placeName: f.place_name ?? f.text ?? '',
                center: [f.center[0], f.center[1]] as [number, number],
              };
            });
          setAddressSuggestions(suggestions);
        })
        .catch((error) => {
          if ((error as Error).name !== 'AbortError') {
            console.error('[SearchBar] address geocode failed:', error);
          }
        });
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [searchMode, searchQuery, searchText, language, map]);

  return (
    <div className="w-full">
      <Combobox value={displayLabel ?? searchText} onChange={onChange}>
        <div className={`relative ${embedded ? '' : 'mt-2'}`}>
          <div
            className={
              embedded
                ? 'items-center relative w-full flex flex-row cursor-default text-left focus:outline-none sm:text-sm'
                : 'items-center relative w-full flex flex-row p-1 cursor-default overflow-hidden rounded-full bg-white text-left shadow-md focus:outline-none sm:text-sm'
            }
            style={
              embedded
                ? undefined
                : {
                    outline: highlight
                      ? `2px solid ${primaryColor}`
                      : '1px solid #d2d6dc',
                  }
            }
          >
            <Combobox.Label className="absolute flex justify-center">
              {(searchText || displayLabel) && (
                <button onClick={clearTextQuery}>
                  <CloseSearchIcon className="w-6 h-6 m-1 text-gray-600" />
                </button>
              )}
              {!searchText && !displayLabel && (
                <SearchIcon className="w-6 h-6 m-1 text-gray-600" />
              )}
            </Combobox.Label>
            <Combobox.Input
              placeholder={translated('search_area')}
              className="w-full rounded-full border-none py-2 pl-8 pr-10 text-sm leading-5 text-gray-900 focus:ring-0"
              displayValue={(e: string) => e}
              onChange={(event) => {
                const s = event.target.value;
                if (displayLabel !== null) {
                  setDisplayLabel(null);
                  if (selectedFilterId) {
                    removeQueryById(selectedFilterId);
                    setSelectedFilterId(null);
                  }
                  if (centerMarker) {
                    centerMarker.remove();
                    setCenterMarker(null);
                  }
                }
                // Immediate local state for snappy input + dropdown filter.
                setSearchTextLocal(s);
                // Debounced parent update — defers the /v3/search round-trip
                // (and the resulting re-render against the 2000-event cache)
                // until the user pauses typing for SEARCH_DEBOUNCE_MS.
                if (debounceTimerRef.current) {
                  clearTimeout(debounceTimerRef.current);
                }
                debounceTimerRef.current = setTimeout(() => {
                  setSearchText(s);
                }, SEARCH_DEBOUNCE_MS);
              }}
            />
            <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
              <ChevronSelectorIcon className="w-6 h-6 text-gray-600" />
            </Combobox.Button>
          </div>
          <Transition
            as={Fragment}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Combobox.Options
              style={{
                position: isMobile ? 'fixed' : 'absolute',
                width: isMobile ? `${width}px` : '390px',
                left: isMobile ? `${left}px` : '-4rem',
                top: isMobile ? `${top + 64}px` : embedded ? '46px' : '54px',
                maxHeight: isMobile ? `${height / 2}px` : '90vh',
              }}
              className={
                'search-penel  shadow-lg overflow-auto rounded-md bg-white py-1 px-2 text-base z-20 focus:outline-none sm:text-sm'
              }
            >
              {loading && (
                <div className="w-full flex p-2 justify-center items-center">
                  {' '}
                  <Spinner />
                </div>
              )}
              {!loading &&
                filteredEvents.length === 0 &&
                regionSuggestions.length === 0 &&
                venueSuggestions.length === 0 &&
                filteredTags.length === 0 &&
                addressSuggestions.length === 0 &&
                searchText !== '' ? (
                <div className="relative cursor-default select-none py-2 px-4 text-gray-700 flex flex-col">
                  {translated('search_nothing')}
                  {!has('global') && (
                    <button
                      onClick={() => {
                        trackMixpanel('SearchBar', 'SearchEverywhere', {
                          searchText,
                        });
                        addQuery({
                          id: 'global',
                          bbox: {
                            //all of austria
                            northEast: {
                              lng: 21.654053,
                              lat: 50.412018,
                            },
                            southWest: {
                              lng: 6.020508,
                              lat: 44.750634,
                            },
                          },
                        });
                      }}
                      style={{ color: primaryColor }}
                    >
                      {translated('search_everywhere')}
                    </button>
                  )}
                </div>
              ) : (
                <>
                  {filteredEvents.map((e) => (
                    <Combobox.Option
                      key={e.id}
                      className={({ active }) =>
                        `search-panel-item ${active ? 'active' : ''
                        } relative cursor-default select-none py-2 pr-4 ${active ? 'bg-balloon text-white' : 'text-gray-900'
                        }`
                      }
                      value={e.id}
                    >
                      {({ selected, active }) => (
                        <>
                          <span
                            className={`flex flex-row items-center truncate ${selected ? 'font-medium' : 'font-normal'
                              }`}
                          >
                            <span className="mx-2">
                              <PiBalloonLight
                                size={24}
                                className={`${active ? 'text-white' : 'text-gray-500'
                                  }`}
                              />
                            </span>
                            {e.title}
                          </span>

                          {selected && (
                            <span
                              className={`absolute inset-y-0 left-0 flex items-center pl-3 ${active ? 'text-white' : 'text-fuchsia-400'
                                }`}
                            >
                              <CheckmarkIcon className="w-6 h-6" />
                            </span>
                          )}
                        </>
                      )}
                    </Combobox.Option>
                  ))}
                  {venueSuggestions.map((v) => (
                    <Combobox.Option
                      key={v.id}
                      className={({ active }) =>
                        `search-panel-item ${active ? 'active' : ''
                        } relative cursor-default select-none py-2 pr-4 ${active ? 'bg-balloon text-white' : 'text-gray-900'
                        }`
                      }
                      value={v.id}
                    >
                      {({ selected, active }) => (
                        <>
                          <span
                            className={`flex flex-row items-center truncate ${selected ? 'font-medium' : 'font-normal'
                              }`}
                          >
                            <span className="mx-2 w-6 h-6">
                              <PiBuildingLight
                                size={24}
                                className={`${active ? 'text-white' : 'text-gray-500'
                                  }`}
                              />
                            </span>
                            <span className="flex flex-col">
                              <span>{v.name}</span>
                              {v.address_locality && (
                                <span
                                  className={`text-xs ${active ? 'text-white/70' : 'text-gray-400'
                                    }`}
                                >
                                  {v.zip_code} {v.address_locality}
                                </span>
                              )}
                            </span>
                          </span>

                          {selected && (
                            <span
                              className={`search-panel-item ${active ? 'active' : ''
                                } absolute inset-y-0 left-0 flex items-center pl-3 ${active ? 'text-white' : 'text-fuchsia-400'
                                }`}
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                strokeWidth="1.5"
                                stroke="currentColor"
                                className="w-6 h-6"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M4.5 12.75l6 6 9-13.5"
                                />
                              </svg>
                            </span>
                          )}
                        </>
                      )}
                    </Combobox.Option>
                  ))}
                  {regionSuggestions.map((e) => (
                    <Combobox.Option
                      key={e.id}
                      className={({ active }) =>
                        `search-panel-item ${active ? 'active' : ''
                        } relative cursor-default select-none py-2 pr-4 ${active ? 'bg-balloon text-white' : 'text-gray-900'
                        }`
                      }
                      value={e.id}
                    >
                      {({ selected, active }) => (
                        <>
                          <span
                            className={`flex flex-row items-center truncate ${selected ? 'font-medium' : 'font-normal'
                              }`}
                          >
                            <span className="mx-2 w-6 h-6">
                              <PiMapPinAreaLight
                                size={24}
                                className={`${active ? 'text-white' : 'text-gray-500'
                                  }`}
                              />
                            </span>
                            {e.name}
                          </span>

                          {selected && (
                            <span
                              className={`search-panel-item ${active ? 'active' : ''
                                } absolute inset-y-0 left-0 flex items-center pl-3 ${active ? 'text-white' : 'text-fuchsia-400'
                                }`}
                            >
                              <CheckmarkIcon className="w-6 h-6" />
                            </span>
                          )}
                        </>
                      )}
                    </Combobox.Option>
                  ))}
                  {filteredTags.map((tag) => (
                    <Combobox.Option
                      key={`tag:${tag.slug}`}
                      className={({ active }) =>
                        `search-panel-item ${active ? 'active' : ''
                        } relative cursor-default select-none py-2 pr-4 ${active ? 'bg-balloon text-white' : 'text-gray-900'
                        }`
                      }
                      value={`tag:${tag.slug}`}
                    >
                      {({ selected, active }) => (
                        <>
                          <span
                            className={`flex flex-row items-center truncate ${selected ? 'font-medium' : 'font-normal'
                              }`}
                          >
                            <span className="mx-2 w-6 h-6">
                              <HashIcon
                                className={`w-6 h-6 ${active ? 'text-white' : 'text-gray-500'
                                  }`}
                              />
                            </span>
                            {tag.name}
                          </span>

                          {selected && (
                            <span
                              className={`search-panel-item ${active ? 'active' : ''
                                } absolute inset-y-0 left-0 flex items-center pl-3 ${active ? 'text-white' : 'text-fuchsia-400'
                                }`}
                            >
                              <CheckmarkIcon className="w-6 h-6" />
                            </span>
                          )}
                        </>
                      )}
                    </Combobox.Option>
                  ))}
                  {addressSuggestions.map((a) => (
                    <Combobox.Option
                      key={a.id}
                      className={({ active }) =>
                        `search-panel-item ${active ? 'active' : ''
                        } relative cursor-default select-none py-2 pr-4 ${active ? 'bg-balloon text-white' : 'text-gray-900'
                        }`
                      }
                      value={a.id}
                    >
                      {({ selected, active }) => (
                        <>
                          <span
                            className={`flex flex-row items-center truncate ${selected ? 'font-medium' : 'font-normal'
                              }`}
                          >
                            <span className="mx-2 w-6 h-6">
                              <PiMapPinLight
                                size={24}
                                className={`${active ? 'text-white' : 'text-gray-500'
                                  }`}
                              />
                            </span>
                            <span className="flex flex-col">
                              <span>{a.name}</span>
                              {a.detail && (
                                <span
                                  className={`text-xs ${active ? 'text-white/70' : 'text-gray-400'
                                    }`}
                                >
                                  {a.detail}
                                </span>
                              )}
                            </span>
                          </span>

                          {selected && (
                            <span
                              className={`absolute inset-y-0 left-0 flex items-center pl-3 ${active ? 'text-white' : 'text-fuchsia-400'
                                }`}
                            >
                              <CheckmarkIcon className="w-6 h-6" />
                            </span>
                          )}
                        </>
                      )}
                    </Combobox.Option>
                  ))}
                </>
              )}
            </Combobox.Options>
          </Transition>
        </div>
      </Combobox>
    </div>
  );
}
